import { typeCheck, toDisplayPrice } from '../lib/util';
import userService from './userService';
import userAddressService from './userAddressService';
import UserAddress from './UserAddress';
import User from './User';
import UserMetadataOption from './UserMetadataOption';

/**
 * SDK interface for user authentication.
 *
 * @namespace Collaterate.User
 */

export default class UserAPI {
    /**
     * Log a user into the storefront.
     *
     * @param {Collaterate.User~loginOptions} loginOptions - Login options object.
     * @returns {Promise<User>} Promise that resolves with a {@link User User}.
     * @throws {CollaterateInputErrorList} If the login credentials are incorrect, will throw a CollaterateInputErrorList type error.
     * @memberof Collaterate.User
     * @example
     * const loginOptions = {
     *     username: document.querySelector('input.username').value,
     *     password: document.querySelector('input.password').value,
     *     rememberMe: document.querySelector('input.rememberMe').value,
     * };
     *
     * Collaterate.User.login(loginOptions)
     *     .then(user => console.log(`${user.username} logged in successfully!`))
     *     .catch(error => console.error('Login unsuccessful!'));
     */

    static async login (loginOptions) {
        typeCheck(loginOptions, Object)

        const {
            username,
            password,
            rememberMe = false,
        } = loginOptions;

        typeCheck(username, String);
        typeCheck(password, String);
        typeCheck(rememberMe, Boolean);

        return new User(await userService.login({ username, password, rememberMe }));
    }

    /**
     * Register a new user with the storefront.
     *
     * @param {Collaterate.User~registrationOptions} registrationOptions - Login options object.
     * @returns {Promise} Promise that resolves on success, there is no response data.
     * @throws {CollaterateInputErrorList} If registration is missing required fields or if any validation errors occur, will throw a CollaterateInputErrorList type error.
     * @memberof Collaterate.User
     * @example
     * const registrationOptions = {
     *     username: document.querySelector('input.username').value,
     *     rememberMe: document.querySelector('input.rememberMe').value,
     *     email: document.querySelector('input.email').value,
     *     firstName: document.querySelector('input.firstName').value,
     *     lastName: document.querySelector('input.lastName').value,
     *     company: document.querySelector('input.company').value,
     *     phone: document.querySelector('input.phone').value,
     *     metadata: [...document.querySelectorAll('.userMetadata')].map(input => ({ label: input.dataset.metadataLabel, value: input.value })),
     * };
     *
     * Collaterate.User.register(registrationOptions)
     *     .then(() => console.log('Registration was successfully submitted. User must check email for verification.'))
     *     .catch(error => console.error('Registration unsuccessful!'));
     */

    static async register (registrationOptions) {
        typeCheck(registrationOptions, Object)

        const {
            username,
            rememberMe = false,
            email = '',
            firstName = '',
            lastName = '',
            company = '',
            phone = '',
            metadata = [],
        } = registrationOptions;

        typeCheck(username.trim(), String);
        typeCheck(rememberMe, Boolean);
        typeCheck(email, String);
        typeCheck(firstName, String);
        typeCheck(lastName, String);
        typeCheck(company, String);
        typeCheck(phone, String);
        typeCheck(metadata, Array);

        const metaDataOptions = {};
        metadata.forEach(m => metaDataOptions[m.label] = m.value);

        return new User(await userService.register({
            username: username.trim(),
            rememberMe,
            email,
            firstName,
            lastName,
            company,
            phone,
            metadata: metaDataOptions,
        }));
    }

    /**
     * Get the currently logged in user.
     *
     * @returns {Promise<User>} Promise that resolves with a {@link User User} or null if there is no logged in user
     * @memberof Collaterate.User
     * @example
     * Collaterate.User.getUser()
     *     .then(user => {
     *         if (!user) {
     *             console.log('No user is logged in.');
     *             return;
     *         }
     *
     *         console.log(`${user.username} is logged in.`);
     *     });
     */
    static async getUser () {
        const userResponse = await userService.getUser();

        return userResponse
            ? new User(userResponse)
            : null;
    }

    /**
     * Get the available user metadata options that can be applied to users of this storefront. These are additional fields defined for this storefront to be used during user registration.
     *
     * @returns {Promise<UserMetadataOption[]>} Promise that resolves with an array of {@link UserMetadataOption UserMetadataOption}s
     * @memberof Collaterate.User
     * @example
     * Collaterate.User.getUserMetadataOptions()
     *     .then(userMetadataOptions => {
     *         userMetadataOptions.forEach(userMetadataOption => {
     *             // Do stuff with the metadata info. Most likely, add fields to the registration form.
     *             console.log(`<label for="metadata${userMetadataOption.id}">${userMetadataOption.label}</label>`)
     *
     *             if (!userMetadataOption.isSelect && !userMetadataOption.isTextarea) {
     *                 console.log(`<input type="${userMetadataOption.type}" id="metadata${userMetadataOption.id}" data-metadata-label="${userMetadataOption.label}" />`);
     *                 return;
     *             }
     *
     *             if (userMetadataOption.isTextarea) {
     *                 console.log(`<textarea id="metadata${userMetadataOption.id}" data-metadata-label="${userMetadataOption.label}"></textarea>`);
     *                 return;
     *             }
     *
     *             if (userMetadataOption.isSelect) {
     *                 console.log(`<select id="metadata${userMetadataOption.id}" data-metadata-label="${userMetadataOption.label}">`);
     *                 userMetadataOption.options.forEach(option => {
     *                     console.log(`<option value="${option.value}">${option.label}</option>`);
     *                 });
     *                 console.log('</select>');
     *                 return;
     *             }
     *         });
     *     });
     */
    static async getUserMetadataOptions () {
        const { metadatas } = await userService.getRegistrationOptions();

        return metadatas.map(metadata => new UserMetadataOption(metadata));
    }

    /**
     * Get a list of jobs that need print files.
     *
     * @param {?number} userId - Storefront User id. Optional. If not provided, this will first make a call to get the current user's id.
     * @returns {Promise<Collaterate.User~job[]>} Promise that resolves with an array of {@link Collaterate.User~job Collaterate.User~job}s.
     * @memberof Collaterate.User
     * @example
     *
     * Collaterate.User.getNeedsPrintFilesItems(userId)
     *     .then(jobs => console.log(jobs.length)); // how many jobs need print files?
     */

    static async getNeedsPrintFilesItems (userId) {
        userId && typeCheck(userId, Number);

        return userService.getNeedsPrintFilesItems(userId);
    }

    /**
     * Get a list of jobs that need review.
     *
     * @param {?number} userId - Storefront User id. Optional. If not provided, this will first make a call to get the current user's id.
     * @returns {Promise<Collaterate.User~job[]>} Promise that resolves with an array of {@link Collaterate.User~job Collaterate.User~job}s.
     * @memberof Collaterate.User
     * @example
     *
     * Collaterate.User.getNeedsReviewItems(userId)
     *     .then(jobs => console.log(jobs.length)); // how many jobs need review?
     */

    static async getNeedsReviewItems (userId) {
        userId && typeCheck(userId, Number);

        return userService.getNeedsReviewItems(userId);
    }

    /**
     * Get a list of order notifications that require action.
     *
     * @param {?number} userId - Storefront User id. Optional. If not provided, this will first make a call to get the current user's id.
     * @returns {Promise<Collaterate.User~orderNotification[]>} Promise that resolves with an array of {@link Collaterate.User~orderNotification Collaterate.User~orderNotification}s.
     * @memberof Collaterate.User
     * @example
     *
     * Collaterate.User.getRequiresActionItems(userId)
     *     .then(jobs => console.log(jobs.length)); // how many requires action order notifications are there?
     */

    static async getRequiresActionItems (userId) {
        userId && typeCheck(userId, Number);

        return userService.getRequiresActionItems(userId);
    }

    /**
     * Mark an order notification as resolved.
     *
     * @param {?number} userId - Storefront User id. Optional. If not provided, this will first make a call to get the current user's id.
     * @param {number} notificationId - The {@link Collaterate.User~orderNotification Collaterate.User~orderNotification} id.
     * @returns {Promise<Collaterate.User~orderNotification[]>} Promise that resolves with an array of remaining {@link Collaterate.User~orderNotification Collaterate.User~orderNotification}s when successful.
     * @memberof Collaterate.User
     * @example
     *
     * Collaterate.User.resolveRequiresActionItem(userId, notificationId)
     *     .then(jobs => console.log(jobs.length)); // how many requires action order notification remain?
     *     .catch(error => console.error(error)));  // something went wrong when resolving the notification.
     */

    static async resolveRequiresActionItem (userId, notificationId) {
        userId && typeCheck(userId, Number);
        typeCheck(notificationId, Number);

        return userService.resolveRequiresActionItem(userId, notificationId);
    }

    /**
     * Get the current user's shipping address book.
     *
     * @returns {Promise<UserAddress[]>} Promise that resolves with an array of {@link UserAddress UserAddress}es.
     * @memberof Collaterate.User
     * @example
     * const addresses = await Collaterate.User.getShippingAddresses();
     *
     * addresses.forEach(address => console.log(`${address.label}: ${address.address}, ${address.city}`));
     */

    static async getShippingAddresses () {
        const addresses = await userAddressService.getShippingAddresses();

        return (addresses || []).map(address => new UserAddress(address));
    }

    /**
     * Get the current user's billing address book.
     *
     * @returns {Promise<UserAddress[]>} Promise that resolves with an array of {@link UserAddress UserAddress}es.
     * @memberof Collaterate.User
     * @example
     * const addresses = await Collaterate.User.getBillingAddresses();
     */

    static async getBillingAddresses () {
        const addresses = await userAddressService.getBillingAddresses();

        return (addresses || []).map(address => new UserAddress(address));
    }

    /**
     * Add an address to the current user's address book.
     *
     * @param {Collaterate.User~addressOptions} addressOptions - The address to add.
     * @returns {Promise<UserAddress>} Promise that resolves with the created {@link UserAddress UserAddress}.
     * @throws {CollaterateInputErrorList} If any field fails validation.
     * @memberof Collaterate.User
     * @example
     * const address = await Collaterate.User.createAddress({
     *     firstName: 'Dan',
     *     lastName: 'Piscitiello',
     *     company: 'The Bernard Group',
     *     address: '315 Walnut St',
     *     city: 'Chanhassen',
     *     stateId: 24,
     *     countryId: 1,
     *     postalCode: '55317',
     *     phone: '555-0100',
     *     addressType: 'SHIP_TO',
     * });
     */

    static async createAddress (addressOptions) {
        typeCheck(addressOptions, Object);

        return new UserAddress(await userAddressService.createAddress(UserAddress.toDB(addressOptions)));
    }

    /**
     * Update an address in the current user's address book.
     *
     * @param {UserAddress} address - The address to update. Must carry the id of an existing address.
     * @param {Collaterate.User~addressOptions} changes - The fields to change.
     * @returns {Promise<UserAddress>} Promise that resolves with the updated {@link UserAddress UserAddress}.
     * @throws {CollaterateInputErrorList} If any field fails validation.
     * @memberof Collaterate.User
     * @example
     * const [address] = await Collaterate.User.getShippingAddresses();
     *
     * await Collaterate.User.updateAddress(address, { phone: '555-0199' });
     */

    static async updateAddress (address, changes = {}) {
        typeCheck(address, UserAddress);
        typeCheck(changes, Object);

        const payload = UserAddress.toUpdateDB({ ...address, ...changes, id: address.id, isDivisionAddress: address.isDivisionAddress });

        return new UserAddress(await userAddressService.updateAddress(address.id, payload));
    }

    /**
     * Delete an address from the current user's address book.
     *
     * @param {UserAddress} address - The address to delete.
     * @returns {Promise} Promise that resolves on success.
     * @memberof Collaterate.User
     * @example
     * await Collaterate.User.deleteAddress(address);
     */

    static async deleteAddress (address) {
        typeCheck(address, UserAddress);

        return userAddressService.deleteAddress(address.id);
    }

    /**
     * Get the shopper's store credit balance **as it stands right now**.
     *
     * This is a live figure, not a historical one. Read against an order that has already been placed it
     * reports what is left today - correct for "you have $X remaining", wrong for a receipt line claiming
     * to show the balance at the time of that order. Nothing in the API records the balance an order was
     * placed against; only the amount applied is kept, on
     * {@link Order#creditAccountsDiscountAmount Order#creditAccountsDiscountAmount}.
     *
     * The balance is scoped by a project, because credit accounts are resolved against that project's
     * division and a project that does not apply credit reports zero. Pass the project the order came
     * from. An {@link Order Order} does not carry its project id, so this cannot currently be called from
     * an order alone - on a confirmation page, use the id the checkout was started with.
     *
     * @param {number} projectId - The id of the project to read the balance against.
     * @returns {Promise<?Collaterate.User~creditBalance>} Returns a Promise that resolves with the shopper's balance, or with null if they have no credit account at all - which is the common case, and different from a zero balance.
     * @throws {TypeError} If projectId is not a number.
     * @throws {CollaterateInputErrorList} If the project does not belong to the signed-in user.
     * @memberof Collaterate.User
     * @example
     * // On a confirmation page, show credit applied and what is left
     * Collaterate.User.getCreditBalance(projectId)
     *     .then(function (credit) {
     *         if (!credit) {
     *             return; // no credit account - hide the row entirely
     *         }
     *
     *         console.log('Applied: $' + order.displayCreditAccountsDiscountAmount);
     *         console.log('Remaining: $' + credit.displayBalance);
     *     });
     */

    static async getCreditBalance (projectId) {
        typeCheck(projectId, Number);

        const data = await userService.getCreditState(projectId);

        if (!data) {
            return null;
        }

        const balance = data.userCreditBalance || 0;

        return Object.freeze({
            balance,
            displayBalance: toDisplayPrice(balance),
        });
    }
}

/**
 * Object containing login information.
 * @typedef {Object} Collaterate.User~loginOptions
 * @property {string} username - The user's username.
 * @property {string} password - The user's password.
 * @property {Boolean} [rememberMe=false] - Remember me. Honestly I don't know what this does. I'll try to find out.
 */

/**
 * Object containing registration information.
 * @typedef {Object} Collaterate.User~registrationOptions
 * @property {string} username - The user's username.
 * @property {Boolean} [rememberMe=false] - Remember me. Honestly I don't know what this does. I'll try to find out.
 * @property {string} email - The user's email.
 * @property {string} firstName - The user's first name.
 * @property {string} lastName - The user's last name.
 * @property {string} [company] - The user's company.
 * @property {string} [phone] - The user's phone number.
 * @property {Collaterate.User~registrationOptionsMetadata[]} [metadata] - Any metadata for this user.
 */

/**
 * Object containing user metadata information.
 * @typedef {Object} Collaterate.User~registrationOptionsMetadata
 * @property {string} label - The label of the {@link UserMetadataOption UserMetadataOption}.
 * @property {string} value - The user selected value for this metadata option.
 */

/**
 * Object describing an address to create or update.
 *
 * @typedef {Object} Collaterate.User~addressOptions
 * @property {string} firstName - First name of the contact at this address.
 * @property {string} lastName - Last name of the contact at this address.
 * @property {string} [company] - Company at this address.
 * @property {string} [phone] - Contact phone number.
 * @property {string} address - Street address.
 * @property {string} [address2] - Second street address line.
 * @property {string} city - City.
 * @property {number} countryId - The country id for this address.
 * @property {number} [stateId] - The state id for this address, where the country has regions.
 * @property {string} postalCode - Postal code.
 * @property {string} [addressType] - The address type: "SHIP_TO", "BILL_TO" or "BLIND_FROM".
 */

/**
 * The shopper's store credit balance.
 *
 * @typedef {Object} Collaterate.User~creditBalance
 * @property {number} balance - Store credit available to spend, as of right now.
 * @property {string} displayBalance - balance formatted with commas and two decimal places.
 */

/**
 * Object containing user job information.
 * @typedef {Object} Collaterate.User~job
 * @property {string} jobName - The job name.
 * @property {number} jobNumber - The job number.
 * @property {string} jobNumberDisplay - The job number as a string.
 * @property {string} jobStorefrontLink - The url to view the order.
 * @property {number} orderNumber - The order number.
 * @property {string} productName - The product name.
 */

/**
 * Object containing user job information.
 * @typedef {Object} Collaterate.User~orderNotification
 * @property {number} id - The notification id.
 * @property {string} message - The notification message. May contain HTML.
 * @property {number} orderNumber - The order number.
 * @property {string} orderHref - The url to the order for the user.
 */
